import { useState, useEffect, useCallback } from 'react';
import { hotelApi } from '../../hotelApi';
import { fmtDate } from '../../dateUtils';

const today = () => new Date().toISOString().split('T')[0];

const EMPTY_MAT = { name: '', unit: 'kg', currentStock: 0, reorderLevel: 0 };
const EMPTY_PURCHASE = { supplierName: '', invoiceNo: '', purchaseDate: today(), totalAmount: '', notes: '', items: [] };
const EMPTY_PITEM = { materialId: '', materialName: '', quantity: '', unit: 'kg', rate: '', amount: '' };
const EMPTY_ISSUE = { materialId: '', quantity: '', unit: 'kg', issueDate: today(), notes: '' };

export default function HotelInventory({ role }) {
  const [tab, setTab]             = useState('stock');
  const [materials, setMaterials] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [issues, setIssues]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showMatForm, setShowMatForm] = useState(false);
  const [showPurchaseForm, setShowPurchaseForm] = useState(false);
  const [showIssueForm, setShowIssueForm]   = useState(false);
  const [matForm, setMatForm]     = useState(EMPTY_MAT);
  const [purForm, setPurForm]     = useState(EMPTY_PURCHASE);
  const [issForm, setIssForm]     = useState(EMPTY_ISSUE);
  const [editMatId, setEditMatId] = useState(null);
  const [saving, setSaving]       = useState(false);
  const [err, setErr]             = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const proms = [];
    if (tab === 'stock') proms.push(hotelApi.getInventory(), Promise.resolve([]), Promise.resolve([]));
    else if (tab === 'purchases') proms.push(hotelApi.getInventory(), hotelApi.getPurchases(), Promise.resolve([]));
    else proms.push(hotelApi.getInventory(), Promise.resolve([]), hotelApi.getKitchenIssues());
    Promise.all(proms).then(([m, p, i]) => {
      setMaterials((m || []).slice().sort((a, b) => a.name.localeCompare(b.name)));
      setPurchases(p || []);
      setIssues(i || []);
    }).finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const fmtRs = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

  // ── Materials ──────────────────────────────────────────────────────────────
  const saveMat = async () => {
    if (!matForm.name.trim()) { setErr('Name required'); return; }
    setSaving(true); setErr('');
    try {
      if (editMatId) await hotelApi.updateMaterial(editMatId, matForm);
      else await hotelApi.createMaterial(matForm);
      setMatForm(EMPTY_MAT); setEditMatId(null); setShowMatForm(false); load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const deleteMat = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    await hotelApi.deleteMaterial(id); load();
  };

  // ── Purchases ──────────────────────────────────────────────────────────────
  const addPurchaseItem = () => setPurForm(f => ({ ...f, items: [...f.items, { ...EMPTY_PITEM }] }));
  const updatePurchaseItem = (i, k, v) => {
    setPurForm(f => {
      const items = [...f.items];
      items[i] = { ...items[i], [k]: v };
      if (k === 'quantity' || k === 'rate') {
        const qty = parseFloat(k === 'quantity' ? v : items[i].quantity) || 0;
        const rate = parseFloat(k === 'rate' ? v : items[i].rate) || 0;
        items[i].amount = (qty * rate).toFixed(2);
      }
      // Auto-fill unit and materialName from selected material
      if (k === 'materialId' && v) {
        const mat = materials.find(m => String(m.id) === String(v));
        if (mat) { items[i].materialName = mat.name; items[i].unit = mat.unit; }
      }
      return { ...f, items };
    });
  };
  const removePurchaseItem = (i) => setPurForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const savePurchase = async () => {
    if (!purForm.supplierName.trim()) { setErr('Supplier name required'); return; }
    if (purForm.items.length === 0) { setErr('Add at least one item'); return; }
    setSaving(true); setErr('');
    try {
      const total = purForm.items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
      await hotelApi.createPurchase({ ...purForm, totalAmount: total });
      setPurForm(EMPTY_PURCHASE); setShowPurchaseForm(false); load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const deletePurchase = async (id) => {
    if (!window.confirm('Delete this purchase and reverse stock?')) return;
    await hotelApi.deletePurchase(id); load();
  };

  // ── Kitchen Issues ──────────────────────────────────────────────────────────
  const saveIssue = async () => {
    if (!issForm.materialId) { setErr('Select a material'); return; }
    if (!issForm.quantity || parseFloat(issForm.quantity) <= 0) { setErr('Quantity required'); return; }
    setSaving(true); setErr('');
    try {
      await hotelApi.createKitchenIssue(issForm);
      setIssForm(EMPTY_ISSUE); setShowIssueForm(false); load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const deleteIssue = async (id) => {
    if (!window.confirm('Delete this issue and restore stock?')) return;
    await hotelApi.deleteKitchenIssue(id); load();
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading inventory…</div>;

  return (
    <div>
      <div className="tabs" style={{ marginBottom: 16 }}>
        <button className={`tab${tab === 'stock' ? ' active' : ''}`} onClick={() => setTab('stock')}>
          📦 Stock ({materials.length})
        </button>
        <button className={`tab${tab === 'purchases' ? ' active' : ''}`} onClick={() => setTab('purchases')}>
          🛒 Purchases
        </button>
        <button className={`tab${tab === 'issues' ? ' active' : ''}`} onClick={() => setTab('issues')}>
          🍳 Kitchen Issues
        </button>
      </div>

      {/* ── Stock Tab ────────────────────────────────────────────── */}
      {tab === 'stock' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Raw Material Stock</div>
            <button className="btn btn-primary" style={{ fontSize: 13, padding: '5px 14px' }}
              onClick={() => { setMatForm(EMPTY_MAT); setEditMatId(null); setShowMatForm(true); }}>
              ➕ Add Material
            </button>
          </div>

          {showMatForm && (
            <div className="card-body" style={{ borderBottom: '1px solid #e8eaed', background: '#f8faf8' }}>
              {err && <div style={{ color: '#c5221f', marginBottom: 8, fontSize: 13 }}>⚠️ {err}</div>}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div className="form-group" style={{ flex: 3, minWidth: 160 }}>
                  <label className="form-label">Material Name *</label>
                  <input className="form-control" value={matForm.name}
                    onChange={e => setMatForm(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div className="form-group" style={{ flex: 1, minWidth: 80 }}>
                  <label className="form-label">Unit</label>
                  <input className="form-control" value={matForm.unit}
                    onChange={e => setMatForm(f => ({ ...f, unit: e.target.value }))} />
                </div>
                <div className="form-group" style={{ flex: 1, minWidth: 100 }}>
                  <label className="form-label">Stock</label>
                  <input className="form-control" type="number" value={matForm.currentStock}
                    onChange={e => setMatForm(f => ({ ...f, currentStock: e.target.value }))} />
                </div>
                <div className="form-group" style={{ flex: 1, minWidth: 100 }}>
                  <label className="form-label">Reorder Level</label>
                  <input className="form-control" type="number" value={matForm.reorderLevel}
                    onChange={e => setMatForm(f => ({ ...f, reorderLevel: e.target.value }))} />
                </div>
                <div style={{ display: 'flex', gap: 8, paddingBottom: 4 }}>
                  <button className="btn btn-primary" onClick={saveMat} disabled={saving}>
                    {saving ? '…' : editMatId ? '💾 Update' : '➕ Add'}
                  </button>
                  <button className="btn btn-secondary"
                    onClick={() => { setShowMatForm(false); setEditMatId(null); setMatForm(EMPTY_MAT); setErr(''); }}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Material</th><th>Unit</th><th>Current Stock</th>
                  <th>Reorder Level</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {materials.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: '#888', padding: 24 }}>No materials yet</td></tr>
                )}
                {materials.map(m => {
                  const isLow = m.reorder_level > 0 && parseFloat(m.current_stock) <= parseFloat(m.reorder_level);
                  return (
                    <tr key={m.id} style={{ background: isLow ? '#fff8f0' : undefined }}>
                      <td><strong>{m.name}</strong></td>
                      <td style={{ fontSize: 13 }}>{m.unit}</td>
                      <td style={{ fontWeight: 700, color: isLow ? '#c5221f' : '#137333' }}>
                        {parseFloat(m.current_stock).toFixed(3)}
                      </td>
                      <td style={{ fontSize: 13 }}>{parseFloat(m.reorder_level).toFixed(3)}</td>
                      <td>
                        {isLow
                          ? <span style={{ background: '#fce8e6', color: '#c5221f', borderRadius: 10, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>⚠️ Low</span>
                          : <span style={{ background: '#e6f4ea', color: '#137333', borderRadius: 10, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>OK</span>
                        }
                      </td>
                      <td>
                        <button className="btn-sm btn-secondary" style={{ marginRight: 4 }}
                          onClick={() => { setMatForm({ name: m.name, unit: m.unit, currentStock: m.current_stock, reorderLevel: m.reorder_level }); setEditMatId(m.id); setShowMatForm(true); }}>Edit</button>
                        <button className="btn-sm btn-danger" onClick={() => deleteMat(m.id, m.name)}>Del</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Purchases Tab ─────────────────────────────────────────── */}
      {tab === 'purchases' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Purchases</div>
            <button className="btn btn-primary" style={{ fontSize: 13, padding: '5px 14px' }}
              onClick={() => { setPurForm(EMPTY_PURCHASE); setShowPurchaseForm(true); }}>
              ➕ New Purchase
            </button>
          </div>

          {showPurchaseForm && (
            <div className="card-body" style={{ borderBottom: '1px solid #e8eaed', background: '#f8faf8' }}>
              {err && <div style={{ color: '#c5221f', marginBottom: 8, fontSize: 13 }}>⚠️ {err}</div>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
                <div className="form-group">
                  <label className="form-label">Supplier *</label>
                  <input className="form-control" value={purForm.supplierName}
                    onChange={e => setPurForm(f => ({ ...f, supplierName: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Invoice No</label>
                  <input className="form-control" value={purForm.invoiceNo}
                    onChange={e => setPurForm(f => ({ ...f, invoiceNo: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Date</label>
                  <input className="form-control" type="date" value={purForm.purchaseDate}
                    onChange={e => setPurForm(f => ({ ...f, purchaseDate: e.target.value }))} />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Notes</label>
                  <input className="form-control" value={purForm.notes}
                    onChange={e => setPurForm(f => ({ ...f, notes: e.target.value }))} />
                </div>
              </div>

              <div style={{ fontWeight: 600, marginBottom: 8 }}>Items:</div>
              {purForm.items.map((item, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ flex: 3, minWidth: 140 }}>
                    {idx === 0 && <label className="form-label">Material</label>}
                    <select className="form-control" value={item.materialId}
                      onChange={e => updatePurchaseItem(idx, 'materialId', e.target.value)}>
                      <option value="">— Select —</option>
                      {materials.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 1, minWidth: 80 }}>
                    {idx === 0 && <label className="form-label">Qty</label>}
                    <input className="form-control" type="number" value={item.quantity}
                      onChange={e => updatePurchaseItem(idx, 'quantity', e.target.value)} />
                  </div>
                  <div className="form-group" style={{ flex: 1, minWidth: 60 }}>
                    {idx === 0 && <label className="form-label">Unit</label>}
                    <input className="form-control" value={item.unit}
                      onChange={e => updatePurchaseItem(idx, 'unit', e.target.value)} />
                  </div>
                  <div className="form-group" style={{ flex: 1, minWidth: 80 }}>
                    {idx === 0 && <label className="form-label">Rate (₹)</label>}
                    <input className="form-control" type="number" value={item.rate}
                      onChange={e => updatePurchaseItem(idx, 'rate', e.target.value)} />
                  </div>
                  <div className="form-group" style={{ flex: 1, minWidth: 80 }}>
                    {idx === 0 && <label className="form-label">Amount</label>}
                    <input className="form-control" readOnly value={item.amount} style={{ background: '#f8f9fa' }} />
                  </div>
                  <button onClick={() => removePurchaseItem(idx)}
                    style={{ background: '#fce8e6', color: '#c5221f', border: 'none', borderRadius: 8,
                      padding: '8px 12px', cursor: 'pointer', fontWeight: 700, marginBottom: 4 }}>×</button>
                </div>
              ))}
              <button onClick={addPurchaseItem}
                style={{ background: '#e6f4ea', color: '#137333', border: 'none', borderRadius: 8,
                  padding: '7px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 13, marginBottom: 12 }}>
                ➕ Add Item
              </button>
              <div style={{ fontWeight: 700, marginBottom: 12, color: '#137333' }}>
                Total: {fmtRs(purForm.items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0))}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" onClick={savePurchase} disabled={saving}>
                  {saving ? 'Saving…' : '💾 Save Purchase'}
                </button>
                <button className="btn btn-secondary"
                  onClick={() => { setShowPurchaseForm(false); setPurForm(EMPTY_PURCHASE); setErr(''); }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th><th>Supplier</th><th>Invoice</th>
                  <th>Total</th><th>Notes</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {purchases.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: '#888', padding: 24 }}>No purchases yet</td></tr>
                )}
                {purchases.map(p => (
                  <tr key={p.id}>
                    <td style={{ fontSize: 13 }}>{fmtDate(p.purchase_date)}</td>
                    <td><strong>{p.supplier_name}</strong></td>
                    <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{p.invoice_no || '—'}</td>
                    <td style={{ fontWeight: 700, color: '#137333' }}>{fmtRs(p.total_amount)}</td>
                    <td style={{ fontSize: 12, color: '#888' }}>{p.notes || '—'}</td>
                    <td>
                      <button className="btn-sm btn-danger" onClick={() => deletePurchase(p.id)}>Del</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Kitchen Issues Tab ──────────────────────────────────────── */}
      {tab === 'issues' && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Kitchen Issues</div>
            <button className="btn btn-primary" style={{ fontSize: 13, padding: '5px 14px' }}
              onClick={() => { setIssForm(EMPTY_ISSUE); setShowIssueForm(true); }}>
              ➕ Issue to Kitchen
            </button>
          </div>

          {showIssueForm && (
            <div className="card-body" style={{ borderBottom: '1px solid #e8eaed', background: '#f8faf8' }}>
              {err && <div style={{ color: '#c5221f', marginBottom: 8, fontSize: 13 }}>⚠️ {err}</div>}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div className="form-group" style={{ flex: 3, minWidth: 160 }}>
                  <label className="form-label">Material *</label>
                  <select className="form-control" value={issForm.materialId}
                    onChange={e => {
                      const mat = materials.find(m => String(m.id) === e.target.value);
                      setIssForm(f => ({ ...f, materialId: e.target.value, unit: mat ? mat.unit : f.unit }));
                    }}>
                    <option value="">— Select —</option>
                    {materials.map(m => <option key={m.id} value={m.id}>{m.name} ({parseFloat(m.current_stock).toFixed(3)} {m.unit})</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ flex: 1, minWidth: 100 }}>
                  <label className="form-label">Quantity *</label>
                  <input className="form-control" type="number" value={issForm.quantity}
                    onChange={e => setIssForm(f => ({ ...f, quantity: e.target.value }))} />
                </div>
                <div className="form-group" style={{ flex: 1, minWidth: 80 }}>
                  <label className="form-label">Unit</label>
                  <input className="form-control" value={issForm.unit}
                    onChange={e => setIssForm(f => ({ ...f, unit: e.target.value }))} />
                </div>
                <div className="form-group" style={{ flex: 1, minWidth: 140 }}>
                  <label className="form-label">Date</label>
                  <input className="form-control" type="date" value={issForm.issueDate}
                    onChange={e => setIssForm(f => ({ ...f, issueDate: e.target.value }))} />
                </div>
                <div className="form-group" style={{ flex: 2, minWidth: 160 }}>
                  <label className="form-label">Notes</label>
                  <input className="form-control" value={issForm.notes}
                    onChange={e => setIssForm(f => ({ ...f, notes: e.target.value }))} />
                </div>
                <div style={{ display: 'flex', gap: 8, paddingBottom: 4 }}>
                  <button className="btn btn-primary" onClick={saveIssue} disabled={saving}>
                    {saving ? '…' : '➕ Issue'}
                  </button>
                  <button className="btn btn-secondary"
                    onClick={() => { setShowIssueForm(false); setIssForm(EMPTY_ISSUE); setErr(''); }}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th><th>Material</th><th>Quantity</th>
                  <th>Unit</th><th>Notes</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {issues.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: '#888', padding: 24 }}>No kitchen issues yet</td></tr>
                )}
                {issues.map(i => (
                  <tr key={i.id}>
                    <td style={{ fontSize: 13 }}>{fmtDate(i.issue_date)}</td>
                    <td><strong>{i.material_name}</strong></td>
                    <td style={{ fontWeight: 600 }}>{parseFloat(i.quantity).toFixed(3)}</td>
                    <td style={{ fontSize: 13 }}>{i.unit}</td>
                    <td style={{ fontSize: 12, color: '#888' }}>{i.notes || '—'}</td>
                    <td>
                      <button className="btn-sm btn-danger" onClick={() => deleteIssue(i.id)}>Del</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
